import { getRuntimeEnvironment } from "../../utils/getRuntimeEnvironment";
import logger from "../../utils/logger";

/**
 * Check if a redirect origin is allowed.
 * Allows:
 * - Same origin (e.g., localhost -> localhost, login.iwoca.co.uk -> login.iwoca.co.uk)
 * - Any iwoca subdomain in the same environment (e.g., *.iwoca.co.uk, *.iwoca-dev.co.uk, *.iwoca.de, *.iwoca-dev.de)
 * - Cross-TLD redirects within same environment (e.g., login.iwoca.co.uk -> www.iwoca.de both production)
 * - localhost -> staging domains (iwoca-dev.co.uk or iwoca-dev.de for local development)
 */
function isAllowedOrigin(redirectOrigin: string): boolean {
  const currentOrigin = window.location.origin;

  // Same origin allowed
  if (redirectOrigin === currentOrigin) {
    return true;
  }

  try {
    const redirectUrl = new URL(redirectOrigin);
    const currentUrl = new URL(currentOrigin);

    // Special case: localhost can redirect to any staging domain (iwoca-dev.co.uk or iwoca-dev.de)
    if (currentUrl.hostname === "localhost") {
      return getRuntimeEnvironment(redirectUrl.hostname) === "staging";
    }

    // Don't allow redirects FROM non-localhost to localhost
    if (redirectUrl.hostname === "localhost") {
      return false;
    }

    const redirectEnvironment = getRuntimeEnvironment(redirectUrl.hostname);
    const currentEnvironment = getRuntimeEnvironment(currentUrl.hostname);

    // Must be in same environment (both production or both staging)
    // This allows cross-TLD redirects (.co.uk -> .de) as long as environment matches
    return redirectEnvironment === currentEnvironment;
  } catch (e) {
    logger.error("Failed to parse redirect origin", {
      redirectOrigin,
      error: e,
    });
    return false;
  }
}

const authServiceDefaultRedirect = {
  production: "https://www.iwoca.co.uk/home/",
  staging: "https://stage.iwoca-dev.co.uk/home/",
};

export function getAuthServiceRedirectTo(): string {
  return (
    getRedirectToLink() ?? authServiceDefaultRedirect[getRuntimeEnvironment()]
  );
}

export function getRedirectToLink() {
  const urlParams = Object.fromEntries(
    new URLSearchParams(window.location.search),
  );
  const { redirect_to, persist_params } = urlParams;
  if (!redirect_to) return undefined;

  const {
    origin,
    pathname,
    searchParams: redirectParams,
  } = new URL(
    redirect_to,
    window.location.origin, // fallback if redirect_to doesn't contain origin
  );

  if (!isAllowedOrigin(origin)) {
    logger.warn("redirect_to has disallowed origin", { origin });
    return undefined;
  }

  if (persist_params !== "1") return redirect_to;

  const queryParams = buildQueryString(redirectParams);

  const targetUrl = `${origin}${pathname}`;
  return queryParams ? `${targetUrl}${queryParams}` : targetUrl;
}

const buildQueryString = (redirectParams: URLSearchParams) => {
  const urlParams = buildWhiteListedSearchParams();

  const allSearchParams = new URLSearchParams({
    ...Object.fromEntries(redirectParams),
    ...Object.fromEntries(urlParams),
  });

  const queryString = allSearchParams.toString();

  return queryString ? "?" + queryString : "";
};

const WHITE_LISTED_PARAMS = [
  "code",
  "hmac",
  "host",
  "shop",
  "state",
  "timestamp",
];

const buildWhiteListedSearchParams = () => {
  const params = new URLSearchParams(window.location.search);
  const hydratedSearchParams = new URLSearchParams();

  for (const WHITE_LISTED_PARAM of WHITE_LISTED_PARAMS) {
    const param = params.get(WHITE_LISTED_PARAM);
    if (param) hydratedSearchParams.append(WHITE_LISTED_PARAM, param);
  }
  return hydratedSearchParams;
};
